import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Package,
  Plus,
  ArrowLeftRight,
  Search,
  Boxes,
  Coins,
  Pencil,
} from "lucide-react";
import {
  useStockValuation,
  useCreateMovement,
  ManualMovementType,
} from "../api/inventory";
import {
  useProducts,
  useCreateProduct,
  useUpdateProduct,
  ListProductsParams,
} from "../api/products";
import { useCategories } from "../api/categories";
import { useAuth } from "../context/AuthContext";
import {
  Product,
  ProductType,
  StockDirection,
  UnitOfMeasure,
} from "../types/models";
import { PageHeader } from "../components/ui/PageHeader";
import { StatCard } from "../components/ui/StatCard";
import { Pagination } from "../components/ui/Pagination";
import { Modal } from "../components/ui/Modal";
import {
  Button,
  SelectField,
  TextField,
  TextareaField,
} from "../components/ui/Form";
import { LoadingState, ErrorState, EmptyState } from "../components/ui/States";
import { toast } from "../components/ui/Toast";
import { getErrorMessage } from "../lib/errors";
import {
  formatCurrency,
  formatNumber,
  humanizeEnum,
  toNumber,
} from "../utils/helpers";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

type ModalState<T> = { open: boolean; product?: T };

export const Inventory: React.FC = () => {
  const { isManagement } = useAuth();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput, 350);
  const [categoryId, setCategoryId] = useState("");
  const [type, setType] = useState<ProductType | "">("");
  const [lowStockOnly, setLowStockOnly] = useState(false);

  const [productModal, setProductModal] = useState<ModalState<Product>>({
    open: false,
  });
  const [movementModal, setMovementModal] = useState<ModalState<Product>>({
    open: false,
  });

  const params: ListProductsParams = {
    page,
    limit: 10,
    search: search || undefined,
    categoryId: categoryId || undefined,
    type: type || undefined,
    sortBy: "name",
    sortOrder: "asc",
  };
  const { data, isLoading, isError, error, refetch, isFetching } =
    useProducts(params);
  const valuation = useStockValuation(isManagement);
  const categories = useCategories();

  const onFilterChange = () => setPage(1);

  // `lowStockOnly` is filtered client-side over the current page since the
  // products endpoint does not expose that flag.
  const visibleItems = (data?.items ?? []).filter((p) =>
    lowStockOnly ? toNumber(p.stockQty) <= toNumber(p.lowStockThreshold) : true,
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Inventory"
        subtitle="Stock levels, valuation and movement ledger."
        actions={
          <>
            {isManagement && (
              <Button
                icon={<Plus size={18} />}
                onClick={() => setProductModal({ open: true })}
              >
                Add Product
              </Button>
            )}
            <Button
              variant="secondary"
              icon={<ArrowLeftRight size={18} />}
              onClick={() => setMovementModal({ open: true })}
            >
              Record Movement
            </Button>
          </>
        }
      />

      {isManagement && valuation.data && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <StatCard
            icon={<Boxes size={22} className="text-blue-400" />}
            label="Active Products"
            value={valuation.data.productCount}
            accent="border-blue-500"
          />
          <StatCard
            icon={<Coins size={22} className="text-emerald-400" />}
            label="Total Stock Value"
            value={formatCurrency(valuation.data.totalValue)}
            hint="At cost price"
            accent="border-emerald-500"
          />
          <StatCard
            icon={<Coins size={22} className="text-amber-400" />}
            label="Finished Goods Value"
            value={formatCurrency(valuation.data.byType?.FINISHED_GOOD)}
            accent="border-amber-500"
          />
        </div>
      )}

      {/* Filters */}
      <div className="card flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              onFilterChange();
            }}
            placeholder="Search by name or SKU…"
            className="input pl-10"
          />
        </div>
        <select
          value={categoryId}
          onChange={(e) => {
            setCategoryId(e.target.value);
            onFilterChange();
          }}
          className="input sm:w-48"
        >
          <option value="">All categories</option>
          {categories.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={type}
          onChange={(e) => {
            setType(e.target.value as ProductType | "");
            onFilterChange();
          }}
          className="input sm:w-44"
        >
          <option value="">All types</option>
          {Object.values(ProductType).map((t) => (
            <option key={t} value={t}>
              {humanizeEnum(t)}
            </option>
          ))}
        </select>
        <label className="flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm text-slate-300">
          <input
            type="checkbox"
            checked={lowStockOnly}
            onChange={(e) => {
              setLowStockOnly(e.target.checked);
              onFilterChange();
            }}
            className="h-4 w-4 rounded border-slate-600 bg-slate-700"
          />
          Low stock only
        </label>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : data && visibleItems.length === 0 ? (
          <EmptyState
            icon={<Package size={32} />}
            title="No products found"
            description="Adjust your filters or add a new product to get started."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700 text-left text-xs uppercase tracking-wide text-slate-400">
                    <th className="px-4 py-3">Product</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3 text-right">In Stock</th>
                    <th className="px-4 py-3 text-right">Price</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody
                  className={isFetching ? "opacity-60 transition-opacity" : ""}
                >
                  {visibleItems.map((p) => {
                    const low =
                      toNumber(p.stockQty) <= toNumber(p.lowStockThreshold);
                    return (
                      <tr
                        key={p.id}
                        className="border-b border-slate-800 last:border-0 hover:bg-slate-800/40"
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-100">
                            {p.name}
                          </div>
                          <div className="text-xs text-slate-500">{p.sku}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-300">
                          {p.category?.name ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-300">
                          {humanizeEnum(p.type)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span
                            className={
                              low
                                ? "font-semibold text-red-400"
                                : "text-slate-200"
                            }
                          >
                            {formatNumber(p.stockQty, 3)} {humanizeEnum(p.unit)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-slate-200">
                          {formatCurrency(p.price)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() =>
                                setMovementModal({ open: true, product: p })
                              }
                              className="rounded-lg p-2 text-slate-400 hover:bg-slate-700 hover:text-slate-100"
                              title="Record movement"
                            >
                              <ArrowLeftRight size={16} />
                            </button>
                            {isManagement && (
                              <button
                                onClick={() =>
                                  setProductModal({ open: true, product: p })
                                }
                                className="rounded-lg p-2 text-slate-400 hover:bg-slate-700 hover:text-slate-100"
                                title="Edit product"
                              >
                                <Pencil size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              meta={lowStockOnly ? undefined : data?.meta}
              onPageChange={setPage}
            />
          </>
        )}
      </div>

      {productModal.open && (
        <ProductFormModal
          product={productModal.product}
          onClose={() => setProductModal({ open: false })}
        />
      )}
      {movementModal.open && (
        <MovementFormModal
          product={movementModal.product}
          products={data?.items ?? []}
          onClose={() => setMovementModal({ open: false })}
        />
      )}
    </div>
  );
};

// ─────────────────────────── Product form ───────────────────────────

const productSchema = z.object({
  name: z.string().min(2, "Name is required"),
  sku: z.string().optional(),
  categoryId: z.string().uuid("Select a category"),
  type: z.nativeEnum(ProductType),
  unit: z.nativeEnum(UnitOfMeasure),
  price: z.coerce.number().nonnegative(),
  costPrice: z.coerce.number().nonnegative(),
  weightGrams: z.coerce.number().positive().optional().or(z.literal("")),
  lowStockThreshold: z.coerce
    .number()
    .nonnegative()
    .optional()
    .or(z.literal("")),
  openingStock: z.coerce.number().nonnegative().optional().or(z.literal("")),
  description: z.string().optional(),
});

const ProductFormModal: React.FC<{
  product?: Product;
  onClose: () => void;
}> = ({ product, onClose }) => {
  const isEdit = Boolean(product);
  const categories = useCategories();
  const create = useCreateProduct();
  const update = useUpdateProduct();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(productSchema),
    defaultValues: {
      name: product?.name ?? "",
      sku: product?.sku ?? "",
      categoryId: product?.categoryId ?? "",
      type: product?.type ?? ProductType.FINISHED_GOOD,
      unit: product?.unit ?? UnitOfMeasure.PIECE,
      price: product ? toNumber(product.price) : 0,
      costPrice: product ? toNumber(product.costPrice) : 0,
      weightGrams: product?.weightGrams
        ? toNumber(product.weightGrams)
        : ("" as never),
      lowStockThreshold: product
        ? toNumber(product.lowStockThreshold)
        : ("" as never),
      openingStock: "" as never,
      description: product?.description ?? "",
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    const num = (v: unknown) =>
      v === "" || v === undefined ? undefined : Number(v);
    try {
      if (isEdit && product) {
        await update.mutateAsync({
          id: product.id,
          input: {
            name: values.name,
            categoryId: values.categoryId,
            type: values.type,
            unit: values.unit,
            price: values.price,
            costPrice: values.costPrice,
            weightGrams: num(values.weightGrams) ?? null,
            lowStockThreshold: num(values.lowStockThreshold),
            description: values.description || undefined,
          },
        });
        toast.success("Product updated");
      } else {
        await create.mutateAsync({
          name: values.name,
          sku: values.sku || undefined,
          categoryId: values.categoryId,
          type: values.type,
          unit: values.unit,
          price: values.price,
          costPrice: values.costPrice,
          weightGrams: num(values.weightGrams),
          lowStockThreshold: num(values.lowStockThreshold),
          openingStock: num(values.openingStock),
          description: values.description || undefined,
        });
        toast.success("Product created");
      }
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  });

  const saving = create.isPending || update.isPending;

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={isEdit ? "Edit Product" : "Add Product"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit} loading={saving}>
            {isEdit ? "Save changes" : "Create product"}
          </Button>
        </>
      }
    >
      <form
        onSubmit={onSubmit}
        className="grid grid-cols-1 gap-4 sm:grid-cols-2"
      >
        <TextField
          label="Name"
          required
          error={errors.name?.message}
          {...register("name")}
        />
        <TextField
          label="SKU"
          placeholder={isEdit ? undefined : "Auto-generated if blank"}
          disabled={isEdit}
          {...register("sku")}
        />
        <SelectField
          label="Category"
          required
          error={errors.categoryId?.message}
          {...register("categoryId")}
        >
          <option value="">Select…</option>
          {categories.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </SelectField>
        <SelectField label="Type" {...register("type")}>
          {Object.values(ProductType).map((t) => (
            <option key={t} value={t}>
              {humanizeEnum(t)}
            </option>
          ))}
        </SelectField>
        <SelectField label="Unit" {...register("unit")}>
          {Object.values(UnitOfMeasure).map((u) => (
            <option key={u} value={u}>
              {humanizeEnum(u)}
            </option>
          ))}
        </SelectField>
        <TextField
          label="Weight (g)"
          type="number"
          step="0.01"
          {...register("weightGrams")}
        />
        <TextField
          label="Selling Price"
          type="number"
          step="0.01"
          error={errors.price?.message}
          {...register("price")}
        />
        <TextField
          label="Cost Price"
          type="number"
          step="0.01"
          error={errors.costPrice?.message}
          {...register("costPrice")}
        />
        <TextField
          label="Low-stock Threshold (warns at low level)"
          type="number"
          step="0.001"
          {...register("lowStockThreshold")}
        />
        {!isEdit && (
          <TextField
            label="Opening Stock"
            type="number"
            step="0.001"
            {...register("openingStock")}
          />
        )}
        <TextareaField
          label="Description"
          className="sm:col-span-2"
          {...register("description")}
        />
      </form>
    </Modal>
  );
};

// ────────────────────────── Movement form ──────────────────────────

const MANUAL_TYPES: ManualMovementType[] = [
  "OPENING",
  "PURCHASE_IN",
  "RETURN",
  "ADJUSTMENT",
];

const movementSchema = z
  .object({
    productId: z.string().uuid("Select a product"),
    type: z.enum(["OPENING", "PURCHASE_IN", "RETURN", "ADJUSTMENT"]),
    quantity: z.coerce.number().positive("Must be greater than zero"),
    direction: z.nativeEnum(StockDirection).optional(),
    unitCost: z.coerce.number().nonnegative().optional().or(z.literal("")),
    note: z.string().optional(),
  })
  .refine((v) => v.type !== "ADJUSTMENT" || v.direction, {
    message: "Direction is required for adjustments",
    path: ["direction"],
  });

const MovementFormModal: React.FC<{
  product?: Product;
  products: Product[];
  onClose: () => void;
}> = ({ product, products, onClose }) => {
  const create = useCreateMovement();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(movementSchema),
    defaultValues: {
      productId: product?.id ?? "",
      type: "PURCHASE_IN",
      quantity: 1,
    },
  });
  const type = watch("type");

  const onSubmit = handleSubmit(async (values) => {
    try {
      await create.mutateAsync({
        productId: values.productId,
        type: values.type,
        quantity: values.quantity,
        direction: values.type === "ADJUSTMENT" ? values.direction : undefined,
        unitCost: values.unitCost === "" ? undefined : Number(values.unitCost),
        note: values.note || undefined,
      });
      toast.success("Stock movement recorded");
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Record Stock Movement"
      description="Production and sales movements are recorded automatically."
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit} loading={create.isPending}>
            Record
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <SelectField
          label="Product"
          required
          error={errors.productId?.message}
          defaultValue={product?.id ?? ""}
          {...register("productId")}
        >
          <option value="">Select…</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.sku})
            </option>
          ))}
        </SelectField>
        <div className="grid grid-cols-2 gap-4">
          <SelectField label="Type" {...register("type")}>
            {MANUAL_TYPES.map((t) => (
              <option key={t} value={t}>
                {humanizeEnum(t)}
              </option>
            ))}
          </SelectField>
          <TextField
            label="Quantity"
            type="number"
            step="0.001"
            required
            error={errors.quantity?.message}
            {...register("quantity")}
          />
        </div>
        {type === "ADJUSTMENT" && (
          <SelectField
            label="Direction"
            required
            error={errors.direction?.message}
            {...register("direction")}
          >
            <option value="">Select…</option>
            <option value={StockDirection.IN}>In (increase)</option>
            <option value={StockDirection.OUT}>Out (decrease)</option>
          </SelectField>
        )}
        <TextField
          label="Unit Cost"
          type="number"
          step="0.01"
          {...register("unitCost")}
        />
        <TextareaField label="Note" {...register("note")} />
      </form>
    </Modal>
  );
};
